import { useEffect } from "react";
import {
  useBlocker,
  useLocation,
  type BlockerFunction,
  type Location,
} from "react-router-dom";
import { useEvent } from "../context/EventProvider";
import { withContext } from "../routes/registry";
import { useLocale, useT } from "../settings/context";
import { readAnswers } from "./answers";
import { composedUnder } from "./answerSession";
import { useSessionKey } from "../auth/useSessionKey";
import { dropsAnswers, unsavedAnswers } from "./unsaved";
import { useArticles } from "./useArticles";
import { useParticipation } from "./useParticipation";

/**
 * ═══ PRÉVENIR AVANT DE PERDRE UNE CORRECTION NON ENREGISTRÉE (bug #258) ═══
 *
 * Quelqu'un de déjà inscrit change une réponse puis s'en va sans « Enregistrer » : la
 * réponse ne vivait que dans l'état de navigation, elle part avec lui. Les deux portes
 * se gardent séparément — `beforeunload` pour l'onglet qu'on ferme ou recharge (c'est
 * la règle du panneau, voir `settings/useAutoSave.ts`), le routeur pour le menu du site,
 * le bouton Précédent et les liens, que `beforeunload` ne voit jamais. Les règles sont
 * dans `funnel/unsaved.ts`.
 *
 * ⚠️ UN SEUL GARDE PAR ÉCRAN : le routeur n'en tient qu'un à la fois. Il est posé par
 * `useFunnelNav`, que toutes les étapes après la participation partagent — y compris
 * celles qui n'écrivent pas elles-mêmes, où une correction emportée par « Continuer »
 * attend encore la confirmation —, et par l'étape participation, qui navigue seule.
 *
 * ⚠️ SEULEMENT EN CORRECTION. Un premier parcours n'a rien d'enregistré à perdre, et ce
 * garde n'a pas à retenir quelqu'un qui renonce à s'inscrire.
 *
 * Les deux portes elles-mêmes sont dans `useUnsavedGuard`, que le carnet de rencontres
 * partage pour sa note non enregistrée (`routes/Contacts.tsx`).
 *
 * Les textes affichés sont français : c'est un produit français.
 */

/**
 * LES ENTRÉES D'HISTORIQUE DONT LES RÉPONSES SONT ÉCRITES.
 *
 * ⚠️ UNE MARQUE, ET NON UNE RELECTURE, parce que la relecture arrive trop tard :
 * « Enregistrer » part à la confirmation dès que l'écriture répond, avant que
 * l'inscription relue ne le dise. Chaque clic suivant crée une autre entrée (`record`
 * remplace, avec une nouvelle clé) : la marque ne couvre donc que ce qui a été écrit.
 */
const written = new Set<string>();

/** À appeler quand les réponses de cette entrée d'historique viennent d'être écrites. */
export function markWritten(key: string): void {
  written.add(key);
}

/**
 * Pose le garde, et rend ce qu'il garde : `true` quand une correction de CET écran attend
 * encore d'être enregistrée.
 *
 * ⚠️ LA MÊME RÈGLE QUE L'AVERTISSEMENT, et pas une seconde : « Ma commande » s'en sert
 * pour ne mener au règlement que lorsque la commande à l'écran est celle en base (bug
 * #412). Deux règles auraient pu dire « rien en attente » et « vos modifications ne sont
 * pas enregistrées » sur le même écran.
 */
export function useLeaveGuard(): boolean {
  const locale = useLocale();
  const event = useEvent();
  const location = useLocation();
  const { recorded } = useParticipation();
  const correction = recorded?.responded === true;
  /* ⚠️ LES RÉPONSES D'UN AUTRE COMPTE NE SONT PAS « À PERDRE » : elles ne s'écriront pas
     (bug 8, voir `answerSession.ts`), et prévenir de leur perte les ferait croire en attente. */
  const session = useSessionKey();
  const ours = (state: unknown) =>
    composedUnder(state, session) ? readAnswers(state) : {};
  const answers = ours(location.state);
  /* Le même cache que l'étape billetterie : aucune requête de plus quand elle l'a lue. */
  const { saved: order } = useArticles(
    correction && answers.articles !== undefined,
    locale,
  );

  const unsaved = (at: Pick<Location, "key" | "state">) =>
    correction &&
    !!recorded &&
    !written.has(at.key) &&
    unsavedAnswers(ours(at.state), { recorded, order });

  useUnsavedGuard(
    unsaved(location),
    ({ currentLocation, nextLocation }) =>
      unsaved(currentLocation) &&
      dropsAnswers({
        answers: ours(currentLocation.state),
        from: currentLocation.pathname,
        to: nextLocation,
        funnel: withContext("/registration", event?.token),
      }),
  );
  return unsaved(location);
}

/**
 * LES DEUX PORTES DE SORTIE, POUR UN ÉCRAN QUI TIENT QUELQUE CHOSE DE NON ENREGISTRÉ.
 *
 * `here` arme `beforeunload` (fermer ou recharger l'onglet) ; `blocks` décide, navigation
 * par navigation, de ce que le routeur retient, et ce qui est retenu se confirme par
 * `confirm`.
 * ⚠️ Un seul appel par écran : le routeur ne tient qu'un garde à la fois.
 */
export function useUnsavedGuard(here: boolean, blocks: BlockerFunction): void {
  const t = useT();

  useEffect(() => {
    if (!here) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [here]);

  const blocker = useBlocker(blocks);

  const question = t(
    "LEAVE_UNSAVED_CHANGES",
    "Vos modifications ne sont pas enregistrées. Quitter cette page les abandonnera. Quitter quand même ?",
  );
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (window.confirm(question)) blocker.proceed();
    else blocker.reset();
  }, [blocker, question]);
}
